/**
 * SearchableMultiSelect — typeahead-filtered multi-select popover.
 *
 * Built for toolbar filters where the default state means "all values" but
 * users can explicitly pick a subset. `values === null/undefined` means all
 * items are selected implicitly. `values.length === 0` means the user has
 * deliberately selected no items.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SearchableMultiSelectItem {
  value: string;
  label: string;
  /** Optional secondary line shown beneath the label in the dropdown. */
  hint?: string;
  disabled?: boolean;
}

export interface SearchableMultiSelectProps {
  items: SearchableMultiSelectItem[];
  /** null/undefined = all items; [] = explicitly no items. */
  values: string[] | null | undefined;
  onChange: (values: string[] | null) => void;
  placeholder?: string;
  noneLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  emptyMessage?: string;
  className?: string;
}

export function SearchableMultiSelect({
  items,
  values,
  onChange,
  placeholder = 'All',
  noneLabel = 'None selected',
  disabled = false,
  invalid = false,
  id,
  emptyMessage = 'No matches',
  className,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const itemValues = useMemo(() => items.filter((i) => !i.disabled).map((i) => i.value), [items]);
  const explicitValues = values ?? null;
  const allImplicit = explicitValues === null;
  const selectedSet = useMemo(() => {
    if (explicitValues === null) return new Set(itemValues);
    return new Set(explicitValues);
  }, [explicitValues, itemValues]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (f === '') return items;
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(f) ||
        (i.hint ? i.hint.toLowerCase().includes(f) : false),
    );
  }, [items, filter]);

  const triggerLabel = useMemo(() => {
    if (explicitValues === null) return placeholder;
    if (explicitValues.length === 0) return noneLabel;
    const labels = explicitValues
      .map((v) => items.find((item) => item.value === v)?.label)
      .filter((label): label is string => Boolean(label));
    if (labels.length === 0) return noneLabel;
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.length} selected`;
  }, [explicitValues, items, noneLabel, placeholder]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    function measure() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    measure();
    window.addEventListener('resize', measure);
    function onScroll() {
      setOpen(false);
      setFilter('');
    }
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
      setFilter('');
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function emitSorted(nextValues: string[]) {
    const order = new Map(items.map((item, index) => [item.value, index]));
    const sorted = [...new Set(nextValues)].sort(
      (a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER),
    );
    onChange(sorted);
  }

  function toggle(item: SearchableMultiSelectItem) {
    if (item.disabled) return;
    if (allImplicit) {
      emitSorted(itemValues.filter((v) => v !== item.value));
      return;
    }
    const next = new Set(explicitValues ?? []);
    if (next.has(item.value)) next.delete(item.value);
    else next.add(item.value);
    emitSorted([...next]);
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setFilter('');
    }
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border bg-surface px-3 pr-8 text-left text-sm text-primary disabled:bg-subtle disabled:text-tertiary disabled:cursor-not-allowed',
          invalid ? 'border-status-danger' : 'border-border-default hover:border-border-strong',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn('truncate', explicitValues !== null && explicitValues.length === 0 && 'text-tertiary')}>
          {triggerLabel}
        </span>
      </button>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
        strokeWidth={1.5}
      />

      {open && panelPos
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[60] rounded-md border border-border-default bg-surface shadow-[var(--shadow-overlay)]"
              style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
            >
              <div className="border-b border-border-default p-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Search…"
                  className="w-full rounded-sm bg-base px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-1 border-b border-border-default px-1.5 py-1">
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="rounded-sm px-2 py-1 text-xs text-secondary hover:bg-subtle hover:text-primary"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-secondary hover:bg-subtle hover:text-primary"
                >
                  <X className="h-3 w-3" strokeWidth={1.5} />
                  Clear
                </button>
              </div>
              <ul role="listbox" className="max-h-64 overflow-y-auto py-1" aria-label={placeholder}>
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-tertiary">{emptyMessage}</li>
                ) : (
                  filtered.map((item) => {
                    const checked = selectedSet.has(item.value);
                    return (
                      <li key={item.value} role="option" aria-selected={checked}>
                        <button
                          type="button"
                          disabled={item.disabled}
                          onClick={() => toggle(item)}
                          className={cn(
                            'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm',
                            item.disabled
                              ? 'cursor-not-allowed text-tertiary'
                              : 'text-primary hover:bg-subtle',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                              checked
                                ? 'border-accent bg-accent text-accent-fg'
                                : 'border-border-default bg-base',
                            )}
                            aria-hidden="true"
                          >
                            {checked ? <Check className="h-3 w-3" strokeWidth={2} /> : null}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate">{item.label}</span>
                            {item.hint ? (
                              <span className="block truncate text-xs text-secondary">{item.hint}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
